"use client";

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import {useRouter} from "next/navigation";

import {PCommentProps} from "@/types/comment";
import {useAppSelector} from "@/redux/hooks";
import {RootState} from "@/redux/store";
import {PATHS} from "@/utils/paths";
import AvatarPlus from "@/components/utilities/avatar-plus";
import CommentStars from "@/components/utilities/comment-stars";

export default function PComment({comment}: PCommentProps) {
    const theme = useTheme();
    const router = useRouter();

    const isAdmin = useAppSelector((state: RootState) =>
        state.user.currentUser?.roles?.includes("Admin")
    );

    const handleClick = () => {
        if (isAdmin) {
            router.push(PATHS.PANEL.USERS.DETAILS(comment.userId));
            return;
        }

        document
            .getElementById(`comment-${comment.id}`)
            ?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
    };

    return (
        <Box
            id={`comment-${comment.id}`}
            className="fade-in"
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    handleClick();
                }
            }}
            display="block"
            p={2}
            borderRadius={2}
            sx={{
                cursor: "pointer",
                textDecoration: "none",
                color: "inherit",
                transition: "background-color 0.3s ease",

                "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                },

                "&:active": {
                    backgroundColor: theme.palette.action.selected,
                },
            }}
        >
            <Box display="flex">
                <AvatarPlus
                    size={50}
                    src={comment.userPicture}
                    name={comment.userFullName}
                />

                <Typography
                    ml={2}
                    variant="body2"
                    color={theme.palette.grey[600]}
                >
                    {comment.userFullName}
                </Typography>
            </Box>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                ml={6}
                mt={-2.5}
            >
                <Box ml={2}>
                    <CommentStars stars={comment.stars}/>
                </Box>

                <Typography
                    fontSize={14}
                    color={theme.palette.grey[800]}
                >
                    {comment.date}
                </Typography>
            </Box>

            <Box ml={7}>
                <Typography>
                    {comment.body}
                </Typography>
            </Box>
        </Box>
    );
}